// Newsletter.jsx
// Newsletter subscription component with backend integration
// Usage: <Newsletter />

import React, { useState } from "react";
import customerApi from "../api/CustomerApi";

const Newsletter = () => {
  const [val, setVal] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!val.trim()) return;

    try {
      setLoading(true);
      setErrorMsg("");

      const response = await customerApi.post("/newsletter/subscribe", {
        email: val,
      });

      if (response.data?.success) {
        setSent(true);
        setVal("");
      }
    } catch (error) {
      console.error("Newsletter subscribe error:", error);

      if (error.response?.data?.errors?.email?.[0]) {
        setErrorMsg(error.response.data.errors.email[0]);
      } else if (error.response?.data?.message) {
        setErrorMsg(error.response.data.message);
      } else {
        setErrorMsg("Subscription failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        /* ── Newsletter ── */
        .ifjona-newsletter {
          background: linear-gradient(100deg, rgba(41,158,96,0.16) 0%, rgba(41,158,96,0.03) 100%);
          border-bottom: 1px solid var(--f-border, rgba(0,0,0,0.08));
          padding: 28px 0;
        }
        .ifjona-newsletter__inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 24px;
          margin: 0 auto;
        }
        .ifjona-newsletter__text {
          flex: 0 0 auto;
        }
        .ifjona-newsletter__badge {
          display: inline-block;
          background: var(--f-green, #299e60);
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          padding: 3px 10px;
          border-radius: 20px;
          margin-bottom: 6px;
        }
        .ifjona-newsletter__headline {
          font-size: 15px;
          font-weight: 700;
          color: var(--f-white, #1a202c);
          margin: 0;
        }
        .ifjona-newsletter__form-wrap {
          flex: 1 1 320px;
          max-width: 520px;
          width: 100%;
        }
        .ifjona-newsletter__form {
          display: flex;
          width: 100%;
          height: 48px;
          border-radius: var(--f-radius, 10px);
          overflow: hidden;
          border: 1.5px solid rgba(41,158,96,0.35);
          background: rgba(255,255,255,0.04);
        }
        .ifjona-newsletter__input {
          flex: 1;
          background: transparent;
          align-items: center;
          border: none;
          outline: none;
          padding: 0 20px;
          font-size: 14px;
          height: 100%;
          color: var(--f-white, #1a202c);
          font-family: inherit;
          min-width: 0;
        }
        .ifjona-newsletter__input::placeholder {
          color: var(--f-muted, #718096);
        }
        .ifjona-newsletter__btn {
          background: var(--f-green, #299e60);
          color: #fff;
          border: none;
          cursor: pointer;
          padding: 0 20px;
          font-size: 13px;
          font-weight: 700;
          height: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s, transform 0.15s;
          white-space: nowrap;
          font-family: inherit;
        }
        .ifjona-newsletter__btn:hover {
          background: var(--f-green-lt, #34c97a);
          transform: translateX(2px);
        }
        .ifjona-newsletter__btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }
        .ifjona-newsletter__success {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--f-green-lt, #34c97a);
          font-weight: 600;
          font-size: 14px;
        }
        .ifjona-newsletter__error {
          margin-top: 8px;
          color: #dc2626;
          font-size: 13px;
          font-weight: 500;
        }

        /* ── Responsive ── */
        @media (max-width: 640px) {
          .ifjona-newsletter__inner {
            flex-direction: column;
            align-items: flex-start;
          }
          .ifjona-newsletter__form-wrap {
            max-width: 100%;
            width: 100%;
            flex: none;
          }
        }
      `}</style>

      <div className="ifjona-newsletter">
        <div className="ifjona-newsletter__inner container container-lg">
          <div className="ifjona-newsletter__text">
            <span className="ifjona-newsletter__badge">Newsletter</span>
            <p className="ifjona-newsletter__headline">
              Get exclusive deals &amp; updates
            </p>
          </div>

          {sent ? (
            <div className="ifjona-newsletter__success">
              <i className="ph-fill ph-check-circle" />
              Thanks! You're subscribed.
            </div>
          ) : (
            <div className="ifjona-newsletter__form-wrap">
              <form className="ifjona-newsletter__form" onSubmit={handleSubmit}>
                <input
                  type="email"
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="ifjona-newsletter__input"
                />
                <button
                  type="submit"
                  className="ifjona-newsletter__btn"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Subscribe"}
                  {!loading && <i className="ph-bold ph-paper-plane-tilt" />}
                </button>
              </form>

              {errorMsg && (
                <div className="ifjona-newsletter__error">{errorMsg}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Newsletter;